import { useRef, useEffect, useCallback, useState } from 'react';

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

const NeuralBackground = ({ scrollProgress = 0 }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const pointsRef = useRef([]);
  const visibleRef = useRef(true);
  const [mobile] = useState(isMobile);

  const initializePoints = useCallback((width, height) => {
    const points = [];
    const cols = mobile ? 14 : 32;
    const rows = mobile ? 9 : 22;

    const centerX = width / 2;
    const centerY = height * 0.6;

    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        const nx = (i / cols - 0.5) * 2;
        const nz = (j / rows - 0.5) * 2;

        const wave1 = Math.sin(nx * Math.PI * 1.5) * Math.cos(nz * Math.PI * 1.5) * 0.3;
        const wave2 = Math.sin(nx * Math.PI * 0.8 + nz * Math.PI * 0.8) * 0.15;
        const ny = wave1 + wave2;

        const fov = 500;
        const z3d = nz * 3 + 2;
        const scale = fov / (fov + z3d * 100);

        const x2d = centerX + nx * width * 0.5 * scale;
        const y2d = centerY + ny * height * 0.3 * scale - nz * height * 0.2 * scale;

        const depth = Math.max(0.2, 1 - (z3d - 2) / 3);

        points.push({
          x: x2d, y: y2d, nx, nz,
          baseX: x2d, baseY: y2d,
          gridI: i, gridJ: j,
          depth, size: 1 + depth * 2.5,
          z3d, scale,
        });
      }
    }

    points.sort((a, b) => b.z3d - a.z3d);
    pointsRef.current = points;
  }, [mobile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    const dpr = mobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
    let width = window.innerWidth;
    let height = window.innerHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    initializePoints(width, height);

    // Pause when off-screen
    const observer = new IntersectionObserver(
      ([entry]) => { visibleRef.current = entry.isIntersecting; },
      { threshold: 0 }
    );
    observer.observe(canvas);

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      initializePoints(width, height);
    };

    window.addEventListener('resize', handleResize);

    let time = 0;
    let frameCount = 0;

    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);

      // Skip frames when off-screen or on mobile
      if (!visibleRef.current) return;
      frameCount++;
      if (mobile && frameCount % 3 !== 0) return;

      time += mobile ? 0.02 : 0.012;

      // Solid fill instead of gradient every frame
      ctx.fillStyle = '#050e1c';
      ctx.fillRect(0, 0, width, height);

      const points = pointsRef.current;
      const len = points.length;

      // Animate wave
      for (let k = 0; k < len; k++) {
        const p = points[k];
        p.y = p.baseY + Math.sin(time + p.nx * 2 + p.nz * 2) * 20 * p.scale;
        p.x = p.baseX + Math.sin(time * 0.5 + p.nz * 1.5) * 8 * p.scale;
      }

      // Draw connections — batch into single path per opacity level
      ctx.lineWidth = 0.8;
      const searchRange = mobile ? 15 : 40;
      ctx.beginPath();
      for (let i = 0; i < len; i++) {
        const p = points[i];
        const end = Math.min(i + searchRange, len);
        for (let j = i + 1; j < end; j++) {
          const o = points[j];
          const gridDistI = Math.abs(p.gridI - o.gridI);
          const gridDistJ = Math.abs(p.gridJ - o.gridJ);

          if (gridDistI <= 1 && gridDistJ <= 1 && (gridDistI + gridDistJ > 0)) {
            const dx = p.x - o.x;
            const dy = p.y - o.y;
            const distSq = dx * dx + dy * dy;
            if (distSq < 10000) {
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(o.x, o.y);
            }
          }
        }
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.stroke();

      // Draw points — batch all into one path
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.beginPath();
      for (let k = 0; k < len; k++) {
        const p = points[k];
        const r = p.size * p.depth;
        ctx.moveTo(p.x + r, p.y);
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      }
      ctx.fill();

      // Desktop only: subtle glow layer
      if (!mobile && frameCount % 2 === 0) {
        ctx.fillStyle = 'rgba(255,255,255,0.04)';
        ctx.beginPath();
        for (let k = 0; k < len; k += 3) {
          const p = points[k];
          const r = p.size * 3;
          ctx.moveTo(p.x + r, p.y);
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        }
        ctx.fill();
      }

      // Vignette (static, doesn't need to be recalculated often)
      if (frameCount % 10 === 1) {
        const centerX = width / 2;
        const centerY = height * 0.48;
        const vg = ctx.createRadialGradient(centerX, centerY, height * 0.15, centerX, centerY, height * 0.9);
        vg.addColorStop(0, 'rgba(5,14,28,0)');
        vg.addColorStop(0.6, 'rgba(5,14,28,0)');
        vg.addColorStop(0.85, 'rgba(5,14,28,0.5)');
        vg.addColorStop(1, 'rgba(5,14,28,0.9)');
        canvas._vignette = vg;
      }
      if (canvas._vignette) {
        ctx.fillStyle = canvas._vignette;
        ctx.fillRect(0, 0, width, height);
      }
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [initializePoints, mobile]);

  const scale = 1 + scrollProgress * 0.25;
  const translateY = -scrollProgress * 10;

  return (
    <div
      className="hero-canvas"
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#050e1c' }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          transform: `scale(${scale}) translateY(${translateY}%)`,
          willChange: 'transform',
        }}
      />
    </div>
  );
};

export default NeuralBackground;
